import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { BreadcrumbsItem } from "./breadcrumbs.types";
import type { Organisation } from "@/types/organisatio";
import { decodeOrgFromToken } from "@/utils/orgToken";

const MAX_VISIBLE = 2;

export const useBreadcrumbs = (isDashboardAvailableCustomer: boolean) => {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();

  const orgBreadcrumbs = ref<BreadcrumbsItem[] | null>(null);

  const isOrgScoped = (path: string) =>
    /^\/(system|msp|customer)\//i.test(path);

  const buildTokenOrgCrumbs = async (): Promise<BreadcrumbsItem[] | null> => {
    const token =
      (store.getters["authentication/token"] as
        | { access_token?: string }
        | string
        | undefined) || null;
    const access = typeof token === "string" ? token : token?.access_token;
    const tokenOrg = decodeOrgFromToken(access || null);
    if (!tokenOrg?.uuid) return null;

    const fallbackType = (tokenOrg.type_name || "customer").toLowerCase();
    try {
      const org =
        (await store.dispatch("organisations/getOrgWithAncestors", {
          uuid: tokenOrg.uuid,
        })) ||
        ({
          uuid: tokenOrg.uuid,
          name: tokenOrg.name || "Organisation",
          organisation_type: tokenOrg.type_name || "Customer",
        } as Organisation);
      const type = (org.organisation_type || fallbackType).toLowerCase();
      const url =
        type === "customer" && !isDashboardAvailableCustomer
          ? `/${type}/${org.uuid}/email_security/overview`
          : `/${type}/${org.uuid}/dashboard`;
      return [
        {
          name: org.name || "Organisation",
          url,
        },
      ];
    } catch {
      const url =
        fallbackType === "customer" && !isDashboardAvailableCustomer
          ? `/${fallbackType}/${tokenOrg.uuid}/email_security/overview`
          : `/${fallbackType}/${tokenOrg.uuid}/dashboard`;
      return [
        {
          name: tokenOrg.name || "Organisation",
          url,
        },
      ];
    }
  };

  const loadOrgAncestors = async () => {
    const path = route.path;
    const match = path.match(/^\/(system|msp|customer)\//i);
    if (!match) {
      orgBreadcrumbs.value = await buildTokenOrgCrumbs();
      return;
    }
    const orgType = match[1].toLowerCase();
    const orgUuid = route.params?.orgUuid as string | undefined;
    // Root special-case: allow /system|/msp|/customer without orgUuid
    if (!orgUuid) {
      const capitalize = (s: string) =>
        s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

      const crumbs: BreadcrumbsItem[] = [
        { name: capitalize(orgType), url: `/${orgType}/dashboard` },
      ];

      crumbs.push({ name: "Dashboard", url: "" });
      orgBreadcrumbs.value = crumbs;
      return;
    }
    if (!orgUuid) {
      orgBreadcrumbs.value = null;
      return;
    }
    try {
      const org = await store.dispatch("organisations/getOrgWithAncestors", {
        uuid: orgUuid,
      });
      if (org) {
        const ancestorCrumbs =
          org?.ancestors?.map((a: Organisation) => {
            const type = (a.organisation_type || orgType).toLowerCase();
            const url =
              type === "customer" && !isDashboardAvailableCustomer
                ? `/${type}/${a.uuid}/email_security/overview`
                : `/${type}/${a.uuid}/dashboard`;
            return {
              name: a.name || "Unknown",
              url,
            };
          }) || [];
        const selfType = (org.organisation_type || orgType).toLowerCase();
        const selfUrl =
          selfType === "customer" && !isDashboardAvailableCustomer
            ? `/${selfType}/${org.uuid}/email_security/overview`
            : `/${selfType}/${org.uuid}/dashboard`;
        const selfCrumb = {
          name:
            store.getters["authentication/selectedCustomer"]?.name ||
            org.name ||
            "Unknown",
          url: selfUrl,
        };
        orgBreadcrumbs.value = [...ancestorCrumbs, selfCrumb];
      } else {
        orgBreadcrumbs.value = null;
      }
    } catch {
      orgBreadcrumbs.value = null;
    }
  };

  watch(
    () => route.fullPath,
    () => {
      loadOrgAncestors();
    },
    { immediate: true },
  );

  const suffixCrumbs = computed(() => {
    const pathSegments = route.path.split("/").filter(Boolean);
    const capitalize = (s: string) =>
      s.replace(/[-_]/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

    // Non-org routes: build crumbs from full path
    if (!isOrgScoped(route.path)) {
      let current = "";
      return pathSegments.map((segment, idx) => {
        current += `/${segment}`;
        const isLast = idx === pathSegments.length - 1;
        return {
          name: capitalize(segment),
          url: isLast ? "" : current,
        } as BreadcrumbsItem;
      });
    }

    if (pathSegments.length < 3) return [];
    const [orgType, orgUuid, ...rest] = pathSegments; // ["system|msp|customer", orgUuid, ...]
    let current = `/${orgType}/${orgUuid}`;
    return rest.map((segment, idx) => {
      current += `/${segment}`;
      const isLast = idx === rest.length - 1;
      const name = capitalize(segment);
      return { name, url: isLast ? "" : current } as BreadcrumbsItem;
    });
  });

  // Suffix crumbs (path segments after the orgUuid) should always be visible
  const suffixVisible = computed(() => suffixCrumbs.value);

  const baseCrumbs = computed<BreadcrumbsItem[]>(() => {
    if (orgBreadcrumbs.value?.length) return orgBreadcrumbs.value;
    return [];
  });

  const items = computed<BreadcrumbsItem[]>(() => {
    const base = baseCrumbs.value;
    const suffix = suffixVisible.value;
    if (!base.length) return suffix;
    const lastBase = base[base.length - 1];
    const filteredSuffix = suffix.filter(
      (crumb) =>
        crumb.name?.toLowerCase() !== lastBase?.name?.toLowerCase() ||
        crumb.url !== lastBase?.url,
    );
    return [...base, ...filteredSuffix];
  });

  const hiddenItems = computed(() => {
    // Collapse only the ancestor chain between first and last ancestor; suffix segments stay visible
    if (!baseCrumbs.value.length) return [];
    const ancestors = baseCrumbs.value;
    const shouldCollapse = ancestors.length > MAX_VISIBLE + 1;
    if (!shouldCollapse) return [];
    // keep first and last ancestor visible; hide the middle ancestors
    return ancestors.slice(1, ancestors.length - 1);
  });

  const visibleItems = computed(() => {
    const base = baseCrumbs.value ?? [];
    const suffix = suffixVisible.value;

    // If no ancestors loaded yet, show nothing until data arrives
    if (!base.length) return [];

    const result: BreadcrumbsItem[] = [];
    const first = base[0];
    const lastAncestor = base[base.length - 1];
    const shouldCollapse = base.length > MAX_VISIBLE + 1;

    if (!shouldCollapse) {
      result.push(...base);
    } else {
      result.push(first);
      result.push({
        name: "...",
        url: "",
        isCollapsed: true,
      } as BreadcrumbsItem & { isCollapsed: boolean });
      result.push(lastAncestor);
    }

    // append suffix segments (current page path), always visible
    const filteredSuffix = suffix.filter(
      (crumb) =>
        !base.some(
          (b) =>
            b.name?.toLowerCase() === crumb.name?.toLowerCase() &&
            b.url === crumb.url,
        ),
    );
    result.push(...filteredSuffix);

    // De-duplicate identical crumbs (name+url)
    const deduped = result.filter(
      (crumb, idx, arr) =>
        idx ===
        arr.findIndex(
          (c) => c.name === crumb.name && (c.url || "") === (crumb.url || ""),
        ),
    );

    // If result too short (no suffix), ensure last ancestor is present
    if (
      deduped[deduped.length - 1]?.url !== lastAncestor.url &&
      suffix.length === 0
    ) {
      deduped.push(lastAncestor);
    }

    return deduped;
  });

  const isCollapsedOpen = ref(false);

  const onHiddenClick = async (hidden: BreadcrumbsItem) => {
    isCollapsedOpen.value = false;
    if (!hidden.url) return;
    await router.push(hidden.url);
  };

  const onBlurCollapse = (event: FocusEvent) => {
    const next = event.relatedTarget as HTMLElement | null;
    if (!next || !event.currentTarget) {
      isCollapsedOpen.value = false;
      return;
    }
    const container = event.currentTarget as HTMLElement;
    if (!container.contains(next)) {
      isCollapsedOpen.value = false;
    }
  };

  return {
    items,
    visibleItems,
    hiddenItems,
    isCollapsedOpen,
    onHiddenClick,
    onBlurCollapse,
  };
};

export default useBreadcrumbs;
